// Account-level API wrappers — the caller's own `core.user` row and the email
// addresses tied to it. These endpoints are org-less: they describe the person
// signed in, not anything inside an organization, so BOTH the admin SPA
// (Settings → Profile) and the signup portal (/me) read and write them.
//
// The routes live under /api/v1/portal/* for historical reasons (the portal
// shipped first) but carry no portal-specific semantics and are registered on
// the same mux as the rest of the API. `@/portal/api` re-exports everything
// here so portal callers keep their existing import path.
import { apiFetch } from './client';

// AccountProfile is the server's view of the caller's core.user row.
// Personnummer is never returned in full — only the masked last-four.
export interface AccountProfile {
  id: string;
  email: string;
  display_name: string;
  first_name: string;
  last_name: string;
  phone: string;
  birth_date: string | null;
  gender: string;
  personnummer_last_four: string;
  has_personnummer: boolean;
  // birth_date is computed from the stored national ID and must be rendered
  // read-only: the ID encodes the date, so offering a second editable source
  // for the same fact lets the two drift apart. The server overrides whatever
  // birth_date a client sends whenever it can derive one.
  birth_date_derived: boolean;
  postal_code: string;
  city: string;
  country_code: string;
  language_code: string;
  marketing_email_opt_in: boolean;
  profile_complete: boolean;
}

// AccountProfileUpsert is the POST payload. The server UPDATEs every column it
// covers, so this is a full replace, NOT a patch — callers must send the whole
// object (read-modify-write) or unsent fields are cleared. `email` is absent by
// design: the login address comes from the verified OIDC token and changes only
// through the email-change flow.
//
// `personnummer` is the one exception to the replace rule: an empty string
// leaves any stored value untouched, so a caller that doesn't collect it can
// safely always send ''.
export interface AccountProfileUpsert {
  first_name: string;
  last_name: string;
  phone: string;
  birth_date: string | null;
  gender: string;
  personnummer: string;
  postal_code: string;
  city: string;
  country_code: string;
  language_code: string;
  marketing_email_opt_in: boolean;
}

export async function getAccountProfile(token: string | null): Promise<AccountProfile> {
  return apiFetch<AccountProfile>('/api/v1/portal/me', { token });
}

export async function updateAccountProfile(
  token: string | null,
  body: AccountProfileUpsert,
): Promise<AccountProfile> {
  return apiFetch<AccountProfile>('/api/v1/portal/me', { method: 'POST', body, token });
}

// toUpsert projects a fetched profile back onto the upsert shape, so a caller
// changing one field can resend the rest unchanged instead of clearing it.
// personnummer is deliberately '' — see AccountProfileUpsert.
export function toUpsert(p: AccountProfile): AccountProfileUpsert {
  return {
    first_name: p.first_name,
    last_name: p.last_name,
    phone: p.phone,
    birth_date: p.birth_date,
    gender: p.gender,
    personnummer: '',
    postal_code: p.postal_code,
    city: p.city,
    country_code: p.country_code,
    language_code: p.language_code,
    marketing_email_opt_in: p.marketing_email_opt_in,
  };
}

// One email address the user owns. Only VERIFIED addresses are used to match
// member rows a club registered under an old or alternative address, so an
// unverified row here links nothing.
export interface UserEmail {
  id: string;
  email: string;
  verified: boolean;
  is_primary: boolean;
}

export async function listEmails(token: string | null): Promise<UserEmail[]> {
  const res = await apiFetch<{ emails: UserEmail[] }>('/api/v1/portal/me/emails', { token });
  return res.emails;
}

// addEmail registers an address (unverified) and triggers verification. In dev
// (no email delivery wired) the server returns verify_token so the address can
// be confirmed without an email; in production the token is withheld and a
// confirmation email is expected instead.
export async function addEmail(
  token: string | null,
  email: string,
): Promise<UserEmail & { verify_token?: string }> {
  return apiFetch<UserEmail & { verify_token?: string }>('/api/v1/portal/me/emails', {
    method: 'POST',
    body: { email },
    token,
  });
}

export async function verifyEmail(token: string | null, verifyToken: string): Promise<void> {
  await apiFetch<void>('/api/v1/portal/me/emails/verify', {
    method: 'POST',
    body: { token: verifyToken },
    token,
  });
}

export async function setPrimaryEmail(token: string | null, id: string): Promise<void> {
  await apiFetch<void>(`/api/v1/portal/me/emails/${encodeURIComponent(id)}/primary`, {
    method: 'POST',
    token,
  });
}

export async function removeEmail(token: string | null, id: string): Promise<void> {
  await apiFetch<void>(`/api/v1/portal/me/emails/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    token,
  });
}
